/**
 * Seguradoras de automóvel (Fase 4 — sinistro na OS, lado da oficina).
 *
 * Lista fixa, decisão do dono do produto (24/09): com texto livre, "Porto",
 * "Porto Seguro" e "PORTO" viram três seguradoras no relatório de quanto cada
 * uma deve, que é justamente o número que o gestor cobra. O que não está aqui
 * entra como "Outra", com o nome digitado.
 *
 * A proteção veicular (associação) não é seguradora, mas paga a oficina do
 * mesmo jeito — autoriza o conserto, o associado paga a cota de participação e
 * a associação paga o resto dias depois — e é uma fatia grande do sinistro de
 * funilaria no Brasil. Entra como tipo próprio, também com o nome digitado,
 * porque são centenas de associações.
 *
 * A seguradora como PARCEIRO (portal, autorização no sistema) é outra coisa e
 * continua travada até haver uma seguradora piloto (D8 do plano).
 */
export const INSURER_KEYS = [
  'porto',
  'azul',
  'itau',
  'bradesco',
  'allianz',
  'tokio_marine',
  'hdi',
  'mapfre',
  'yelum',
  'zurich',
  'suhai',
  'youse',
  'protecao_veicular',
  'outra',
] as const;

export type InsurerKey = (typeof INSURER_KEYS)[number];

const INSURER_LABELS: Record<InsurerKey, string> = {
  porto: 'Porto Seguro',
  azul: 'Azul Seguros',
  itau: 'Itaú Seguros',
  bradesco: 'Bradesco Seguros',
  allianz: 'Allianz',
  tokio_marine: 'Tokio Marine',
  hdi: 'HDI',
  mapfre: 'Mapfre',
  yelum: 'Yelum (ex-Liberty)',
  zurich: 'Zurich',
  suhai: 'Suhai',
  youse: 'Youse',
  protecao_veicular: 'Proteção veicular',
  outra: 'Outra',
};

/** As chaves que exigem o nome digitado. */
export const INSURERS_WITH_NAME: readonly InsurerKey[] = ['protecao_veicular', 'outra'];

/** Tamanho do nome digitado para "Outra" / proteção veicular. */
export const INSURER_NAME_MAX = 80;
/** Número do sinistro (ou do evento, na proteção veicular). */
export const CLAIM_NUMBER_MAX = 40;

export function isInsurerKey(value: unknown): value is InsurerKey {
  return typeof value === 'string' && (INSURER_KEYS as readonly string[]).includes(value);
}

export function insurerNeedsName(key: InsurerKey): boolean {
  return INSURERS_WITH_NAME.includes(key);
}

/**
 * O nome para mostrar: o da lista, ou o digitado quando a chave pede nome.
 * "Proteção veicular — Associação X" diz as duas coisas ao cliente.
 */
export function insurerLabel(key: string, name?: string | null): string {
  if (!isInsurerKey(key)) return name?.trim() || key;
  const typed = name?.trim();
  if (key === 'outra') return typed || INSURER_LABELS.outra;
  if (key === 'protecao_veicular') return typed ? `${INSURER_LABELS.protecao_veicular} — ${typed}` : INSURER_LABELS.protecao_veicular;
  return INSURER_LABELS[key];
}

/** Opções da lista, na ordem da tela. */
export const INSURER_OPTIONS: ReadonlyArray<{ key: InsurerKey; label: string }> = INSURER_KEYS.map((key) => ({
  key,
  label: INSURER_LABELS[key],
}));

/** O que a seguradora decidiu sobre um item do orçamento. */
export const ITEM_COVERAGES = ['covered', 'declined'] as const;
export type ItemCoverage = (typeof ITEM_COVERAGES)[number];

export function isItemCoverage(value: unknown): value is ItemCoverage {
  return typeof value === 'string' && (ITEM_COVERAGES as readonly string[]).includes(value);
}

/**
 * Prazo padrão para a seguradora pagar, contado da conclusão da OS, quando a
 * oficina não informou o vencimento. É o meio do ciclo de 30 a 60 dias que o
 * plano descreve; o gestor ajusta por sinistro.
 */
export const INSURER_DEFAULT_TERM_DAYS = 30;

/** Status do título contra a seguradora — derivado na API (`lib/insurance-claim.ts`). */
export const INSURER_RECEIVABLE_STATUSES = ['nothing_due', 'awaiting_service', 'open', 'partial', 'overdue', 'paid'] as const;
export type InsurerReceivableStatus = (typeof INSURER_RECEIVABLE_STATUSES)[number];

export const INSURER_RECEIVABLE_STATUS_LABELS: Record<InsurerReceivableStatus, string> = {
  nothing_due: 'Nada a receber',
  awaiting_service: 'Vence 30 dias após a conclusão',
  open: 'Em aberto',
  partial: 'Parcial',
  overdue: 'Vencido',
  paid: 'Pago',
};
